/**
 * BallHolderBanner
 *
 * Small, non-blocking accountability banner naming the person who currently
 * holds the ball for a Catchball loop. Mounted on working surfaces (Track
 * detail, Track workspace) so anyone configuring participation, the item
 * pool, criteria, value scales, or values knows who is accountable for the
 * tier right now.
 *
 * Possession lives on catchball_loops.ball_holder_user_id (written only by
 * the claim/release/transition RPCs); this banner is a read-only mirror.
 * It renders nothing when there is no active loop, the loop is resolved
 * (closed / finally rejected), or the ball is unclaimed — it informs, it
 * never blocks.
 */

import { Hand } from 'lucide-react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { normalizeLoopState } from '../../../types/prioritization/catchballLoop';

export function BallHolderBanner({
  loopId,
  tierLabel,
}: {
  /** The Catchball loop whose holder should be named. Renders nothing when absent. */
  loopId: string | null | undefined;
  /** Optional label for the tier currently holding the ball (e.g. an org-unit name). */
  tierLabel?: string | null;
}) {
  const loop = useCatchballLoopStore((s) => (loopId ? s.getLoop(loopId) ?? null : null));
  const { users: systemUsers } = useSystemUsers();

  if (!loop || !loop.ballHolderUserId) return null;

  const canonical = normalizeLoopState(loop.state);
  if (canonical === 'DECLINED' || canonical === 'FINAL_REJECTED') return null;

  const holder = systemUsers.find((u) => u.id === loop.ballHolderUserId);
  const holderName = holder?.name || holder?.email;
  if (!holderName) return null;

  return (
    <div className="flex items-center gap-2.5 border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 px-4 py-2.5 text-xs text-[var(--text-sec)]">
      <Hand className="h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
      <p className="min-w-0">
        <span className="font-semibold">{holderName}</span> currently holds the ball for this
        tier{tierLabel ? ` (${tierLabel})` : ''} — responsible for participation, item pool,
        criteria, value scales, and values.
      </p>
    </div>
  );
}

export default BallHolderBanner;
